import React from 'react';
import { Controller, useFormContext } from 'react-hook-form';
import useTranslate from '../../../core/languages/hooks/useTranslate';
import useCurrentLanguage from '../../../core/languages/hooks/useCurrentLanguage';
import useTransLocationsGetCollectByTransportAndCityId from '../../hooks/useTransLocationsGetCollectByTransportAndCityId';
import TransportLocation from '../../types/TransportLocation';
import { getTransportLocationLabel } from '../../utils/transportLabels';
import LocationDetailsInput from './LocationDetailsInput';
import { TransportEnum } from '../../enums/TransferTransportEnum';

const DepartureLocationTo: React.FC = () => {
    const t = useTranslate();
    const languageCode = useCurrentLanguage();
    const { control, formState: { errors }, getValues, watch } = useFormContext();
    
    const transportRaw = getValues('departureTransport');
    const transport = transportRaw ? Number(transportRaw) : null;
    const city = getValues('city');
    const selectedLocationId = watch('departureLocationTo');
    const departureLocationFromId = watch('departureLocationFrom');
    const hasTransport = transport !== undefined && transport !== null;
    const hasFromLocation = departureLocationFromId !== undefined && departureLocationFromId !== null && departureLocationFromId !== '';
    const shouldExecute = hasTransport && !!city && city !== '';
    
    const { data: transportLocations = [], isLoading } = useTransLocationsGetCollectByTransportAndCityId(
        transport,
        city,
        { execute: shouldExecute }
    );

    // Filter only locations that have requiredMoreDetails true or hotel not null - only if OTHER transport
    // Order: first locations with hotel, then locations with requiredMoreDetails
    const filteredLocations = Number(transport) === TransportEnum.OTHER
        ? transportLocations.filter(
            (location: TransportLocation) => location.requiredMoreDetails
        )
        : transportLocations;
    // Find the selected location to know if it requires more details
    const selectedLocation = filteredLocations.find(loc => String(loc.id) === String(selectedLocationId));
    const requiresDetails = selectedLocation?.requiredMoreDetails || false;

    return (
        <div>
            <Controller
                name="departureLocationTo"
                control={control}
                rules={{ required: hasTransport && hasFromLocation ? t('common.feedback.field_required') : false }}
                render={({ field }) => (
                    <div>
                        <label className="block text-sm font-medium text-neutral-700 dark:text-neutral-300 mb-2">
                            {getTransportLocationLabel(t, transport, 'departureTo')}
                        </label>
                        <select
                            {...field}
                            disabled={!hasTransport || !hasFromLocation || isLoading}
                            className="w-full px-4 py-2 border border-neutral-200 dark:border-neutral-700 rounded-lg bg-white
                            dark:bg-neutral-900 text-neutral-900 dark:text-white focus:outline-none focus:ring-2
                            focus:ring-blue-500 disabled:opacity-50 disabled:cursor-not-allowed"
                        >
                            <option value="">
                                {!hasTransport ? t('transfers.message.selectTransportFirst') : 
                                 !hasFromLocation ? t('transfers.message.selectDepartureLocationFromFirst') :
                                 (isLoading ? t('common.actions.loading') : t('common.actions.select'))}
                            </option>
                            {filteredLocations.map((location: TransportLocation) => (
                                <option key={location.id} value={location.id}>
                                    {location.trName[languageCode] || location.trName.en}
                                </option>
                            ))}
                        </select>
                        {errors.departureLocationTo && (
                            <p className="mt-1 text-sm text-red-600">
                                {errors.departureLocationTo.message}
                            </p>
                        )}
                    </div>
                )}
            />
            {requiresDetails && (
                <LocationDetailsInput
                    locationId={selectedLocationId}
                    fieldName="departureLocationToDetails"
                    label={t('transfers.fields.departureLocationToDetails')}
                />
            )}
        </div>
    );
};

export default DepartureLocationTo;
